/**
 * OrdersPageModern — native Modern layout for the customer orders list.
 * Shares all data + reorder logic with the Code page via useCustomerOrders().
 */
import { Link } from 'react-router-dom';
import { Package, Clock, CheckCircle, XCircle, Truck, RotateCcw, ChevronRight, ShoppingBag } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatOrderStatus } from '../../utils/orderStatus';
import { useCustomerOrders } from './OrdersPage';

const STATUS_STYLE = {
  pending:          { icon: Clock,       bg: 'var(--am-inset)',       fg: 'var(--am-ink-2)' },
  confirmed:        { icon: Package,     bg: 'var(--am-emerald-50)',  fg: 'var(--am-emerald-700)' },
  preparing:        { icon: Package,     bg: 'var(--am-emerald-50)',  fg: 'var(--am-emerald-700)' },
  out_for_delivery: { icon: Truck,       bg: 'var(--am-emerald-600)', fg: '#FFFFFF' },
  delivered:        { icon: CheckCircle, bg: 'var(--am-emerald-50)',  fg: 'var(--am-emerald-700)' },
  cancelled:        { icon: XCircle,     bg: '#FEF2F2',               fg: '#B91C1C' },
};

const FILTER_OPTIONS = [
  ['active', 'All active'], ['pending', 'Pending'], ['confirmed', 'Confirmed'], ['out_for_delivery', 'Out for delivery'],
  ['history', 'All history'], ['delivered', 'Delivered'], ['cancelled', 'Cancelled'],
];

export function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const Icon = s.icon;
  return (
    <span className="am-chip shrink-0" style={{ background: s.bg, color: s.fg }} data-testid="order-status-chip">
      <Icon className="w-3.5 h-3.5" />{formatOrderStatus(status)}
    </span>
  );
}

function SegTabs({ value, onChange, counts }) {
  const tabs = [['active', 'Active', counts.active], ['history', 'History', counts.history], ['all', 'All', counts.all]];
  return (
    <div className="am-seg" data-testid="orders-segmented">
      {tabs.map(([key, label, count]) => (
        <button key={key} type="button" onClick={() => onChange(key)} data-testid={`orders-tab-${key}`}
          className={`am-seg-btn ${value === key ? 'am-active' : ''}`}>
          {label} <span className="am-tnum" style={{ color: 'var(--am-ink-3)' }}>{count}</span>
        </button>
      ))}
    </div>
  );
}

function StatusFilter({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44 h-10 rounded-full bg-white border-[var(--am-hair-strong)] text-[var(--am-ink-1)]" data-testid="status-filter">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-[var(--am-hair)] rounded-xl" style={{ fontFamily: 'var(--am-font-sans)' }}>
        {FILTER_OPTIONS.map(([v, label]) => (
          <SelectItem key={v} value={v} className="text-[var(--am-ink-1)] rounded-lg">{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function OrderCard({ order, reorderEnabled, onReorder }) {
  const itemCount = (order.items || []).length;
  const canReorder = reorderEnabled && ['delivered', 'cancelled'].includes(order.status);
  return (
    <div className="am-card am-card-hover p-5" data-testid={`order-row-${order.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="am-label">Order #{order.id.slice(0, 8).toUpperCase()}</p>
          <p className="text-sm mt-1" style={{ color: 'var(--am-ink-3)' }}>
            {new Date(order.created_at || order.delivered_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>
        <StatusChip status={order.status} />
      </div>
      <div className="flex items-end justify-between gap-3 mt-4">
        <div className="min-w-0">
          <p className="text-2xl font-semibold am-tnum" style={{ color: 'var(--am-ink-1)' }}>${Number(order.total || 0).toFixed(2)}</p>
          <p className="text-sm mt-0.5" style={{ color: 'var(--am-ink-2)' }}>
            {itemCount} item{itemCount === 1 ? '' : 's'}{order.payment_method && <> · <span className="capitalize">{order.payment_method}</span></>}
          </p>
        </div>
      </div>
      {order.delivery_address && (
        <p className="text-xs mt-3 truncate" style={{ color: 'var(--am-ink-3)' }}>{order.delivery_address}</p>
      )}
      <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t" style={{ borderColor: 'var(--am-hair)' }}>
        {canReorder && (
          <button type="button" onClick={() => onReorder(order)} className="am-btn-ghost" data-testid={`reorder-${order.id}`}>
            <RotateCcw className="w-4 h-4" />Reorder
          </button>
        )}
        <Link to={`/customer/orders/${order.id}`} className="am-cta inline-flex items-center gap-1 px-4 py-2 text-sm" data-testid={`view-order-${order.id}`}>
          {order.status === 'out_for_delivery' ? 'Track order' : 'View details'}<ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ filter, onReset }) {
  const copy = filter === 'active'
    ? ['No active orders', "You'll see live tracking here once you place one."]
    : filter === 'history' ? ['No completed orders yet', 'Delivered and cancelled orders will show up here.']
    : ['No orders found', 'Try a different filter.'];
  return (
    <div className="am-card text-center px-6 py-12" data-testid="orders-empty">
      <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: 'var(--am-emerald-50)' }}>
        <ShoppingBag className="w-6 h-6" style={{ color: 'var(--am-emerald-600)' }} />
      </div>
      <p className="am-section-title mt-4">{copy[0]}</p>
      <p className="text-sm mt-1" style={{ color: 'var(--am-ink-3)' }}>{copy[1]}</p>
      {filter === 'active' ? (
        <Link to="/customer" className="am-cta inline-flex items-center gap-1 px-5 py-2.5 mt-6 text-sm" data-testid="orders-empty-shop">
          Shop now<ChevronRight className="w-4 h-4" />
        </Link>
      ) : filter !== 'history' && (
        <button type="button" onClick={onReset} className="am-btn-ghost mt-6" data-testid="orders-empty-reset">Back to active</button>
      )}
    </div>
  );
}

function OrdersSkeleton() {
  return (
    <div className="space-y-4" data-testid="orders-loading">
      {[0, 1, 2].map((i) => <div key={i} className="am-card h-40 animate-pulse" style={{ background: 'var(--am-card-alt)' }} />)}
    </div>
  );
}

export function OrdersPageModern() {
  const {
    statusFilter, setStatusFilter, reorderEnabled, orders, loading,
    reorderFromPastOrder, filteredOrders, activeCount, historyCount,
  } = useCustomerOrders();

  return (
    <div className="am-modern-scope min-h-screen" data-testid="orders-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="orders-page-modern">
        <div className="flex items-center gap-3 mb-6">
          <BackButton to="/customer" />
          <div>
            <p className="am-eyebrow">Your account</p>
            <h1 className="text-3xl font-semibold tracking-tight" data-testid="orders-title">Orders</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <SegTabs value={statusFilter} onChange={setStatusFilter}
            counts={{ active: activeCount, history: historyCount, all: orders.length }} />
          {statusFilter !== 'all' && <StatusFilter value={statusFilter} onChange={setStatusFilter} />}
        </div>
        {loading ? <OrdersSkeleton /> : (
          <div className="space-y-4 am-stagger">
            {filteredOrders.map((o) => (
              <OrderCard key={o.id} order={o} reorderEnabled={reorderEnabled} onReorder={reorderFromPastOrder} />
            ))}
            {filteredOrders.length === 0 && <EmptyState filter={statusFilter} onReset={() => setStatusFilter('active')} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrdersPageModern;
